import type { PhoenixPushMessage, Version } from '@core/phoenix/core/types'

export const HEADER_LENGTH = 1
export const META_LENGTH = 4
export const KINDS = { push: 0, reply: 1, broadcast: 2 }

function isBinaryMessage(
  msg: PhoenixPushMessage,
): msg is PhoenixPushMessage<ArrayBuffer> {
  return msg.payload?.constructor === ArrayBuffer
}

/** Encodes the structured message object to a JSON string or binary. */
export function encode(
  msg: PhoenixPushMessage,
  vsn: Version,
  callback: (payload: string | ArrayBuffer) => void,
) {
  switch (vsn) {
    case '1.0.0': {
      return callback(JSON.stringify(msg))
    }
    case '2.0.0': {
      if (isBinaryMessage(msg)) {
        return callback(binaryEncode(msg))
      }
      const data = [msg.join_ref, msg.ref, msg.topic, msg.event, msg.payload]
      return callback(JSON.stringify(data))
    }
  }
}

function isBinaryPayload(
  payload: string | ArrayBuffer,
): payload is ArrayBuffer {
  return payload.constructor === ArrayBuffer
}

/** Parses the payload into a structured message object. */
export function decode(
  rawPayload: string | ArrayBuffer,
  vsn: Version,
  callback: (payload: unknown) => void,
) {
  switch (vsn) {
    case '1.0.0': {
      return callback(JSON.parse(rawPayload as string))
    }
    case '2.0.0': {
      if (isBinaryPayload(rawPayload)) {
        return callback(binaryDecode(rawPayload))
      }
      const [join_ref, ref, topic, event, payload] = JSON.parse(rawPayload)
      return callback({ join_ref, ref, topic, event, payload })
    }
  }
}

function binaryEncode(message: PhoenixPushMessage<ArrayBuffer>) {
  const { join_ref = '', ref, event, topic, payload } = message
  const metaLength =
    META_LENGTH + join_ref.length + ref.length + topic.length + event.length
  const header = new ArrayBuffer(HEADER_LENGTH + metaLength)
  const view = new DataView(header)
  let offset = 0

  view.setUint8(offset++, KINDS.push)
  view.setUint8(offset++, join_ref.length)
  view.setUint8(offset++, ref.length)
  view.setUint8(offset++, topic.length)
  view.setUint8(offset++, event.length)
  Array.from(join_ref, (char) => view.setUint8(offset++, char.charCodeAt(0)))
  Array.from(ref, (char) => view.setUint8(offset++, char.charCodeAt(0)))
  Array.from(topic, (char) => view.setUint8(offset++, char.charCodeAt(0)))
  Array.from(event, (char) => view.setUint8(offset++, char.charCodeAt(0)))

  const combined = new Uint8Array(header.byteLength + payload.byteLength)
  combined.set(new Uint8Array(header), 0)
  combined.set(new Uint8Array(payload), header.byteLength)

  return combined.buffer
}

function binaryDecode(buffer: ArrayBufferLike) {
  const view = new DataView(buffer)
  const kind = view.getUint8(0)
  const decoder = new TextDecoder()
  switch (kind) {
    case KINDS.push:
      return decodePush(buffer, view, decoder)
    case KINDS.reply:
      return decodeReply(buffer, view, decoder)
    case KINDS.broadcast:
      return decodeBroadcast(buffer, view, decoder)
  }
}

function decodePush(
  buffer: ArrayBufferLike,
  view: DataView,
  decoder: TextDecoder,
) {
  const joinRefSize = view.getUint8(1)
  const topicSize = view.getUint8(2)
  const eventSize = view.getUint8(3)
  let offset = HEADER_LENGTH + META_LENGTH - 1 // pushes have no ref
  const joinRef = decoder.decode(buffer.slice(offset, offset + joinRefSize))
  offset = offset + joinRefSize
  const topic = decoder.decode(buffer.slice(offset, offset + topicSize))
  offset = offset + topicSize
  const event = decoder.decode(buffer.slice(offset, offset + eventSize))
  offset = offset + eventSize
  const data = buffer.slice(offset, buffer.byteLength)
  return {
    join_ref: joinRef,
    ref: null,
    topic: topic,
    event: event,
    payload: data,
  }
}

function decodeReply(
  buffer: ArrayBufferLike,
  view: DataView,
  decoder: TextDecoder,
) {
  const joinRefSize = view.getUint8(1)
  const refSize = view.getUint8(2)
  const topicSize = view.getUint8(3)
  const eventSize = view.getUint8(4)
  let offset = HEADER_LENGTH + META_LENGTH
  const joinRef = decoder.decode(buffer.slice(offset, offset + joinRefSize))
  offset = offset + joinRefSize
  const ref = decoder.decode(buffer.slice(offset, offset + refSize))
  offset = offset + refSize
  const topic = decoder.decode(buffer.slice(offset, offset + topicSize))
  offset = offset + topicSize
  const event = decoder.decode(buffer.slice(offset, offset + eventSize))
  offset = offset + eventSize
  const data = buffer.slice(offset, buffer.byteLength)
  const payload = { status: event, response: data }
  return {
    join_ref: joinRef,
    ref: ref,
    topic: topic,
    event: 'phx_reply',
    payload: payload,
  }
}

function decodeBroadcast(
  buffer: ArrayBufferLike,
  view: DataView,
  decoder: TextDecoder,
) {
  const topicSize = view.getUint8(1)
  const eventSize = view.getUint8(2)
  let offset = HEADER_LENGTH + 2
  const topic = decoder.decode(buffer.slice(offset, offset + topicSize))
  offset = offset + topicSize
  const event = decoder.decode(buffer.slice(offset, offset + eventSize))
  offset = offset + eventSize
  const data = buffer.slice(offset, buffer.byteLength)

  return {
    join_ref: null,
    ref: null,
    topic: topic,
    event: event,
    payload: data,
  }
}
